"use client";

// about — who innernet is, and why the thing is shaped like this.
//
// THE BODY ONLY. This used to be a page of its own (AboutClient) with its own
// head, its own crumb and its own creature, so selecting "about" in the company
// rail LEFT the company: a full reload landed you on a different-looking page
// whose title was pinned where the rail had been. It is a tab now, read inside
// the same rail as the letters and the releases (components/company/CompanyClient
// mounts this under the head), and the rail collapses on scroll exactly as it
// does everywhere else. The room is still the dark one — netti IS the room —
// the shell just keeps its shape.
//
// THE COPY (2026-09-30) is the team's own, from the notion page "about & team
// copy" (marketing rules & guides). Two passages of the page it replaced are
// kept because they say something the new copy does not: that we own no model,
// and what we owe you structurally. The figures are AboutFigures: cream sheets
// on the night ground, the community letter's and the guides' language.

import Link from "next/link";
import { PillButton } from "../ui";
import BrandIcon from "../BrandIcon";
import AppIcon from "../brand/AppIcon";
import { FigBecoming, FigBoundaries, FigGuests, FigOneMemory, FigPast, FigUnderneath } from "./AboutFigures";

/** the tools a memory has to outlive — the shelf, at rest. */
const SHELF = ["claude", "openai", "cursor", "codex", "windsurf", "gemini", "zed", "cline"];

function Pull({ big, small }: { big: string; small: string }) {
  return (
    <blockquote className="ab-pull">
      <p className="ab-pull__big">{big}</p>
      <p className="ab-pull__small">{small}</p>
    </blockquote>
  );
}

export default function AboutBody({ signedIn = false }: { signedIn?: boolean }) {
  return (
    <div className="co-body ab-body">
      {/* the headline the page used to pin — now the first thing under the rail */}
      <h1 className="ab-title os-enter" style={{ ["--i" as string]: 0 }}>
        the inner world matters.
      </h1>

      <section className="ab-sec">
        <p className="ab-lede">most of what makes a person who they are never becomes visible.</p>
        <ul className="ab-verse">
          <li>the thoughts they never wrote down.</li>
          <li>the ideas they abandoned.</li>
          <li>the questions they kept returning to.</li>
          <li>the reasons behind a decision.</li>
          <li>the moment something finally clicked.</li>
          <li>the version of themselves they were before they changed.</li>
        </ul>
      </section>

      <section className="ab-sec">
        <p>we tend to preserve the things that come out of us.</p>
        <ul className="ab-verse ab-verse--pairs">
          <li>the song, <span>not the eleven versions before it.</span></li>
          <li>the book, <span>not the pages that were thrown away.</span></li>
          <li>the shipped product, <span>not the argument that nearly killed it.</span></li>
          <li>the decision, <span>not the weeks of uncertainty that produced it.</span></li>
        </ul>
        <p>but the outcome is only the surface.</p>
        <p className="ab-beat">the real story is everything that happened inside before it.</p>
      </section>

      <FigUnderneath />

      <section className="ab-sec">
        <p>
          for most of history, that inner world was difficult to preserve. thoughts disappeared when
          the person did. conversations ended. ideas were forgotten. the reasoning behind a decision
          became a story remembered differently years later.
        </p>
        <p>
          we could record what people made. we could rarely record{" "}
          <strong>how they became the person who made it.</strong>
        </p>
        <p className="ab-beat">that is changing.</p>
        <p>
          our thinking now happens through machines that can see it as it unfolds. they witness the
          questions, experiments, contradictions, discarded ideas, decisions, and reasoning behind
          them. for the first time, the inner half of our work can become part of our memory.
        </p>
        <p>
          <strong>but seeing it is not the same as remembering it.</strong> today, that context is
          scattered across conversations, applications, models, and sessions. when one ends, much of
          what happened inside it disappears with it.
        </p>
        <p className="ab-beat">innernet exists to preserve that continuity.</p>
      </section>

      <Pull big="one memory, yours" small="the tools come and go. the context underneath remains." />

      <section className="ab-sec">
        <h2 className="ab-h">context should have continuity.</h2>
        <p>
          innernet is a persistent context layer between you and the ai tools you think with. through
          mcp and an open api, the ai tools you use can read from and write to the same memory.
        </p>
        <p>
          claude. cursor. codex. chatgpt. your own agents. whatever comes next. you connect once. your
          context follows.
        </p>
      </section>

      <FigOneMemory />

      <section className="ab-sec">
        <p className="ab-beat">
          the memory does not belong to the model. <span style={{ color: "var(--ink-3)" }}>it belongs to you.</span>
        </p>
      </section>

      <section className="ab-sec">
        <h2 className="ab-h">memory is more than retrieval.</h2>
        <p>
          a larger search index can tell you what you said. we want to preserve what your thinking
          became.
        </p>
        <p>
          innernet organises context into maps containing the ideas, people, decisions, projects, and
          knowledge that make up your world. commits preserve change. branches preserve possibility.
        </p>
        <p>
          and at the centre of it is <strong>netti</strong>. netti is the intelligence that tends your
          memory. it watches what accumulates across your work, connects things that belong together,
          captures what matters, notices what has changed, and turns scattered interactions into
          lasting context. you do not have to manually maintain your memory.
        </p>
        <ul className="ab-verse">
          <li>a conversation can become a decision.</li>
          <li>a decision can become part of a project.</li>
          <li>a project can develop a history.</li>
          <li>a history can become part of how your ai understands you.</li>
        </ul>
      </section>

      <FigBecoming />

      <section className="ab-sec">
        <p className="ab-beat">netti turns what happens into what is remembered.</p>
      </section>

      <Pull big="the past is part of the context" small="what you changed your mind about matters too." />

      <section className="ab-sec">
        <h2 className="ab-h">a position has a history.</h2>
        <p>
          most systems move toward the latest answer. something new arrives. something old gets
          overwritten. the record of changing your mind disappears.
        </p>
        <p>
          but a person&rsquo;s context is not a single snapshot. the idea you rejected six months ago
          may become useful again. a decision makes more sense when you can see what came before it. a
          contradiction can reveal more than a conclusion.
        </p>
        <p>that is why innernet is versioned.</p>
      </section>

      <FigPast />

      <section className="ab-sec">
        <p className="ab-beat">memory should preserve becoming, not just being.</p>
      </section>

      <section className="ab-sec">
        <h2 className="ab-h">the memory belongs to the person.</h2>
        <p>
          your context should not become a side effect of whichever application you happen to use.
          innernet sits outside the tools.
        </p>
        <p>
          each model is a guest. each application is a client.{" "}
          <strong>netti is the one that carries the continuity between them.</strong>
        </p>
        <ul className="ab-verse">
          <li>move from claude to cursor.</li>
          <li>from chatgpt to codex.</li>
          <li>from one project to another.</li>
          <li>from one model to the next.</li>
        </ul>
        <p>
          your memory does not start over. you can connect different tools to it, build on top of it,
          and take your context with you.
        </p>
      </section>

      <FigGuests />

      <section className="ab-sec">
        <p>
          which is why we own no model and resell none. neutrality is easy to claim and hard to hold
          when your revenue depends on routing tokens through your own thing. ours doesn&rsquo;t, and
          it never will.
        </p>
        <p className="ab-beat">
          we are not building another place where your thoughts live.{" "}
          <span style={{ color: "var(--ink-3)" }}>we are building the intelligence that remembers them.</span>
        </p>
      </section>

      <section className="ab-sec">
        <h2 className="ab-h">privacy should exist at the level of memory.</h2>
        <p>
          when an ai can access your context, the important question is not simply whether the data is
          encrypted. it is <strong>what this particular tool is allowed to know.</strong>
        </p>
        <p>
          innernet gives facts different levels of access: private, self, trusted, work, and public.
          permissions are enforced when memory is read. not every tool gets to know everything about
          you.
        </p>
      </section>

      <Pull big="private · self · trusted · work · public" small="context should travel with boundaries." />

      <FigBoundaries />

      <section className="ab-sec">
        <h2 className="ab-h">what we owe you</h2>
        <p>
          a memory has a survivorship requirement other software does not. if a notes app shuts down
          you lose a notes app; if a memory shuts down you lose the record. so the promises that count
          are structural.
        </p>
        <ul className="ab-list">
          <li>you can export everything, right now, without asking anyone.</li>
          <li>deletion is real and immediate; backups are purged within thirty days.</li>
          <li>
            your memory is reachable over an open endpoint by tools we do not control, so it does not
            depend on us being the only door to it.
          </li>
          <li>your maps are never sold, and never train anyone&rsquo;s model.</li>
        </ul>
      </section>

      <section className="ab-sec">
        <h2 className="ab-h">built for a world where the tools keep changing.</h2>
        <ul className="ab-verse">
          <li>the models will change.</li>
          <li>the interfaces will change.</li>
          <li>the applications will change.</li>
        </ul>
        <p>your context should not have to start over every time they do.</p>
        <div className="ab-shelf" aria-label="the tools a memory has to outlive">
          {SHELF.map((n) => (
            <AppIcon key={n} name={n} size={44} />
          ))}
        </div>
        <p>
          we believe memory will become infrastructure for the ai era in the same way the web became
          infrastructure for information. innernet is our attempt at building that layer.
        </p>
        <p className="ab-beat">a persistent memory for the things you think, build, learn, and become.</p>
      </section>

      <section className="ab-close">
        <p className="ab-close__line">
          the outcome was always going to survive.
          <span>we are here for the other half.</span>
        </p>
        <div className="gateway-row">
          <PillButton
            as="a"
            href={signedIn ? "/dashboard" : "/signup?from=about"}
            icon={<BrandIcon name="innernet" size={17} className="pill-netti" />}
          >
            {signedIn ? "open your innernet" : "create yours"}
          </PillButton>
          <Link className="quiet-link" href="/company/compare">how we compare</Link>
          <Link className="quiet-link" href="/company/team">who we are</Link>
        </div>
        {!signedIn && <p className="ab-close__note">free to start, no card.</p>}
      </section>
    </div>
  );
}
